import type {
  User, Label, Thread, ThreadsListResponse,
  Message, ComposeData, BulkAction, LoginRequest, LoginResponse,
  SearchResult
} from './types'

// VITE_API_URL already includes the /api prefix (e.g. "https://tmailgw.com/api").
// All paths below are relative to that base — they do NOT repeat /api.
const API_BASE = import.meta.env.VITE_API_URL ?? ''

// ─── HTTP Client ──────────────────────────────────────────────────────────────

class APIError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'APIError'
  }
}

let accessToken: string | null = localStorage.getItem('access_token')
let refreshPromise: Promise<string> | null = null

// Injected by App.tsx after the account store is ready.
// Returns the active account's body refresh token so the auto-retry logic can
// refresh via POST body (not just the httpOnly cookie which always belongs to
// the primary/last-logged-in account and may differ from the active account).
let getBodyRefreshToken: (() => string) | null = null
export function setRefreshTokenGetter(fn: () => string) {
  getBodyRefreshToken = fn
}

export function setAccessToken(token: string) {
  accessToken = token
  localStorage.setItem('access_token', token)
}

export function clearAccessToken() {
  accessToken = null
  localStorage.removeItem('access_token')
}

async function refreshToken(): Promise<string> {
  const bodyRT = getBodyRefreshToken?.() ?? ''
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    // Include the active account's body refresh token when available.
    // This is essential for non-primary accounts whose session is tracked by
    // body token (not by the httpOnly cookie, which only carries the last
    // primary-login session).
    ...(bodyRT
      ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refresh_token: bodyRT }) }
      : {}),
  })
  if (!res.ok) throw new APIError(res.status, 'Session expired')
  const data = await res.json()
  setAccessToken(data.access_token)
  return data.access_token
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  retry = true,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  })

  if (res.status === 401 && retry) {
    // Deduplicate concurrent refresh attempts
    if (!refreshPromise) {
      refreshPromise = refreshToken().finally(() => { refreshPromise = null })
    }
    try {
      await refreshPromise
      return request<T>(path, options, false)
    } catch {
      clearAccessToken()
      window.dispatchEvent(new Event('auth:logout'))
      throw new APIError(401, 'Session expired')
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new APIError(res.status, body.error ?? `HTTP ${res.status}`)
  }

  if (res.status === 204) return undefined as T
  return res.json()
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export interface SessionInfo {
  id: string
  ip_address: string
  user_agent: string
  created_at: string
  last_active_at: string
  is_current: boolean
}

export const auth = {
  login: (data: LoginRequest) =>
    request<LoginResponse>('/auth/login', { method: 'POST', body: JSON.stringify(data) }),
  logout: () =>
    request<void>('/auth/logout', { method: 'POST' }),
  me: () =>
    request<User>('/auth/me'),
  updateMe: (data: Partial<{ name: string; current_password: string; new_password: string }>) =>
    request<User>('/auth/me', { method: 'PATCH', body: JSON.stringify(data) }),
  sessions: () =>
    request<{ sessions: SessionInfo[] }>('/auth/sessions'),
  revokeSession: (id: string) =>
    request<void>(`/auth/sessions/${id}`, { method: 'DELETE' }),
  revokeAllOtherSessions: () =>
    request<{ revoked: number }>('/auth/sessions', { method: 'DELETE' }),
  jmapCredentials: () =>
    request<{ jmap_url: string; email: string; password: string }>('/auth/jmap-credentials'),
}

// ─── Labels ───────────────────────────────────────────────────────────────────

export const labels = {
  list: () =>
    request<Label[]>('/labels'),
  create: (data: { name: string; color: string; type: 'label' | 'folder' }) =>
    request<Label>('/labels', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<{ name: string; color: string }>) =>
    request<void>(`/labels/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<void>(`/labels/${id}`, { method: 'DELETE' }),
}

// ─── Threads ──────────────────────────────────────────────────────────────────

export const threads = {
  list: (params: { label?: string; page?: number; per_page?: number }) => {
    const q = new URLSearchParams()
    if (params.label) q.set('label', params.label)
    if (params.page !== undefined) q.set('page', String(params.page))
    if (params.per_page) q.set('per_page', String(params.per_page))
    return request<ThreadsListResponse>(`/threads?${q}`)
  },
  get: (id: string) =>
    request<Thread>(`/threads/${id}`),
  update: (id: string, data: Partial<{ is_read: boolean; is_starred: boolean; is_important: boolean }>) =>
    request<void>(`/threads/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  trash: (id: string) =>
    request<void>(`/threads/${id}`, { method: 'DELETE' }),
  bulk: (data: BulkAction) =>
    request<void>('/threads/bulk', { method: 'POST', body: JSON.stringify(data) }),
}

// ─── Multipart helper (for file uploads) ──────────────────────────────────────

async function requestMultipart<T>(path: string, form: FormData): Promise<T> {
  // Do NOT set Content-Type — the browser sets it with the multipart boundary
  const headers: Record<string, string> = {}
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: form,
    credentials: 'include',
  })

  if (res.status === 401) {
    if (!refreshPromise) {
      refreshPromise = refreshToken().finally(() => { refreshPromise = null })
    }
    try {
      await refreshPromise
      throw new APIError(401, 'Token refreshed — please try again')
    } catch {
      clearAccessToken()
      window.dispatchEvent(new Event('auth:logout'))
      throw new APIError(401, 'Session expired')
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new APIError(res.status, body.error ?? `HTTP ${res.status}`)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

// ─── Compose ──────────────────────────────────────────────────────────────────

export const compose = {
  send: (data: ComposeData) => {
    const files = data.attachments ?? []
    if (files.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { attachments: _a, ...rest } = data
      return request<{ message_id: string }>('/compose/send', {
        method: 'POST',
        body: JSON.stringify(rest),
      })
    }
    // Multipart: embed compose fields as JSON string + append files
    const form = new FormData()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { attachments: _a, ...jsonData } = data
    form.append('data', JSON.stringify(jsonData))
    files.forEach(f => form.append('attachments', f))
    return requestMultipart<{ message_id: string }>('/compose/send', form)
  },
  saveDraft: (data: Omit<ComposeData, 'bcc'>) =>
    request<{ id: string }>('/compose/draft', { method: 'POST', body: JSON.stringify(data) }),
  updateDraft: (id: string, data: Omit<ComposeData, 'bcc'>) =>
    request<void>(`/compose/draft/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteDraft: (id: string) =>
    request<void>(`/compose/draft/${id}`, { method: 'DELETE' }),
}

// ─── Search ───────────────────────────────────────────────────────────────────

export const search = {
  query: (params: {
    q: string
    from?: string
    to?: string
    has_attachment?: boolean
    is_unread?: boolean
    is_starred?: boolean
    after?: string
    before?: string
    label?: string
    page?: number
    per_page?: number
  }) => {
    const q = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v))
    })
    return request<{ results: SearchResult[]; total: number; has_more: boolean }>(`/search?${q}`)
  },
}

// ─── Contacts ─────────────────────────────────────────────────────────────────

export const contacts = {
  autocomplete: (q: string) =>
    request<Array<{ email: string; name: string; avatar_url?: string }>>(
      `/contacts/autocomplete?q=${encodeURIComponent(q)}`
    ),
  list: () =>
    request<Array<{ id: string; email: string; name: string; avatar_url?: string }>>('/contacts'),
}

// ─── Admin ────────────────────────────────────────────────────────────────────

export const admin = {
  stats: () =>
    request<{ stats: Record<string, number>; volume: Array<{ date: string; total: number }> }>('/admin/stats'),
  users: (params?: { q?: string; page?: number; per_page?: number }) => {
    const q = new URLSearchParams()
    if (params?.q) q.set('q', params.q)
    if (params?.page !== undefined) q.set('page', String(params.page))
    if (params?.per_page !== undefined) q.set('per_page', String(params.per_page))
    return request<{ users: Array<Record<string, unknown>>; total: number }>(`/admin/users?${q}`)
  },
  createUser: (data: { email: string; name: string; password: string; role: string; quota_gb?: number }) =>
    request<Record<string, unknown>>('/admin/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id: string, data: Partial<{ name: string; role: string; quota_gb: number; is_active: boolean; password: string }>) =>
    request<void>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteUser: (id: string) =>
    request<void>(`/admin/users/${id}`, { method: 'DELETE' }),
  domains: () =>
    request<{ domains: Array<Record<string, unknown>> }>('/admin/domains'),
  addDomain: (data: { domain: string; is_primary?: boolean; owner_user_id?: string }) =>
    request<Record<string, unknown>>('/admin/domains', { method: 'POST', body: JSON.stringify(data) }),
  syncDomains: () =>
    request<{ imported: string[]; total: number; skipped: number }>('/admin/domains/sync', { method: 'POST' }),
  deleteDomain: (id: string) =>
    request<void>(`/admin/domains/${id}`, { method: 'DELETE' }),
  assignDomain: (id: string, ownerUserId: string | null) =>
    request<{ ok: boolean }>(`/admin/domains/${id}/assign`, {
      method: 'PATCH',
      body: JSON.stringify({ owner_user_id: ownerUserId }),
    }),
  getDns: (id: string) =>
    request<{
      domain: string
      mx_host: string
      mx_priority: number
      zone_file: string
      records: Array<{ name: string; type: string; value: string }>
      spf_ok: boolean
      dkim_ok: boolean
      dmarc_ok: boolean
      mx_ok: boolean
    }>(`/admin/domains/${id}/dns`),
  verifyDomain: (id: string) =>
    request<void>(`/admin/domains/${id}/verify`, { method: 'POST' }),
  auditLog: (page = 0) =>
    request<{ entries: Array<Record<string, unknown>>; total: number }>(`/admin/audit-log?page=${page}`),
  orgSettings: () =>
    request<Record<string, unknown>>('/admin/settings'),
  updateOrgSettings: (data: Record<string, unknown>) =>
    request<void>('/admin/settings', { method: 'PATCH', body: JSON.stringify(data) }),
}

// ─── Superadmin ───────────────────────────────────────────────────────────────

export interface ImpersonationLogEntry {
  id: string
  superadmin_id: string
  superadmin_email: string
  superadmin_name: string
  target_user_id: string
  target_email: string
  started_at: string
  ended_at: string | null
}

export interface UsageRow {
  tenant_id: string
  tenant_name: string
  slug: string
  plan: string
  user_count: number
  total_quota_bytes: number
  used_quota_bytes: number
}

export interface PermissionStatus {
  permission: string
  granted: boolean
}

export const superadmin = {
  stats: () =>
    request<{
      stats: Record<string, number>
      volume: Array<{ date: string; total: number }>
      top_orgs: Array<{ name: string; slug: string; user_count: number }>
    }>('/superadmin/stats'),

  orgs: (params?: { q?: string; page?: number; per_page?: number }) => {
    const q = new URLSearchParams()
    if (params?.q) q.set('q', params.q)
    if (params?.page !== undefined) q.set('page', String(params.page))
    if (params?.per_page !== undefined) q.set('per_page', String(params.per_page))
    return request<{ orgs: Array<Record<string, unknown>>; total: number }>(`/superadmin/orgs?${q}`)
  },

  createOrg: (data: {
    name: string
    slug: string
    plan: string
    max_mailboxes?: number
    max_users?: number
    billing_email?: string
    admin_email: string
    admin_name: string
    admin_password: string
  }) =>
    request<{ id: string; slug: string }>('/superadmin/orgs', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateOrg: (id: string, data: Partial<{
    name: string
    plan: string
    max_users: number
    max_quota_bytes: number
    is_active: boolean
    billing_email: string
  }>) =>
    request<{ ok: boolean }>(`/superadmin/orgs/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  users: (params?: { q?: string; org?: string; page?: number; per_page?: number }) => {
    const q = new URLSearchParams()
    if (params?.q) q.set('q', params.q)
    if (params?.org) q.set('org', params.org)
    if (params?.page !== undefined) q.set('page', String(params.page))
    if (params?.per_page !== undefined) q.set('per_page', String(params.per_page))
    return request<{ users: Array<Record<string, unknown>>; total: number }>(`/superadmin/users?${q}`)
  },

  impersonate: (userId: string) =>
    request<{
      access_token: string
      target_user_id: string
      target_email: string
      expires_in: number
    }>('/superadmin/impersonate', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    }),

  impersonationLog: (page = 0) =>
    request<{ entries: ImpersonationLogEntry[]; total: number; page: number }>(
      `/superadmin/impersonation-log?page=${page}`
    ),

  usage: () =>
    request<{ usage: UsageRow[] }>('/superadmin/usage'),

  billingPlans: () =>
    request<{ plans: Array<Record<string, unknown>> }>('/superadmin/billing/plans'),

  createBillingPlan: (data: {
    name: string
    slug: string
    max_users: number
    max_quota_bytes: number
    price_monthly?: number
    price_yearly?: number
    features?: string[]
  }) =>
    request<{ id: string }>('/superadmin/billing/plans', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateBillingPlan: (id: string, data: Partial<{
    name: string
    max_users: number
    max_quota_bytes: number
    price_monthly: number
    price_yearly: number
    features: string[]
    is_active: boolean
  }>) =>
    request<{ ok: boolean }>(`/superadmin/billing/plans/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteBillingPlan: (id: string) =>
    request<{ ok: boolean }>(`/superadmin/billing/plans/${id}`, { method: 'DELETE' }),

  getAdminPermissions: (adminId: string) =>
    request<{
      user_id: string
      email: string
      name: string
      role: string
      permissions: PermissionStatus[]
    }>(`/superadmin/admins/${adminId}/permissions`),

  setAdminPermissions: (adminId: string, permissions: string[]) =>
    request<{ ok: boolean; granted: number }>(`/superadmin/admins/${adminId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
    }),
}

export { APIError }
